import React, { useEffect, useState } from "react";
import { BadgeCheck, ExternalLink, Star, Users } from "lucide-react";
import api from "@/lib/api";
import { PLATFORMS, CATEGORIES, labelOf } from "./marketingConstants";

function CreatorCard({ c }) {
  return (
    <article className="glass-panel p-5 flex flex-col gap-3 hover:-translate-y-1 transition-transform" data-testid={`creator-card-${c.id}`}>
      <div className="flex items-center gap-3">
        {c.avatar_url
          ? <img src={c.avatar_url} alt={c.display_name} className="h-14 w-14 rounded-full object-cover border border-lifted-green/40" loading="lazy" />
          : <div className="h-14 w-14 rounded-full bg-lifted-green/10 border border-lifted-green/30 flex items-center justify-center font-anton text-xl text-lifted-green">{(c.display_name || "?")[0]}</div>}
        <div className="min-w-0">
          <h3 className="font-bebas uppercase tracking-wider text-lg text-white truncate">{c.display_name}</h3>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em]">
            <span className="text-lifted-green inline-flex items-center gap-1" data-testid="creator-badge">
              <BadgeCheck className="h-3 w-3" /> {c.badge === "affiliate" ? "Lifted Affiliate" : "Creator"}
            </span>
            {c.featured && <span className="text-amber-300 inline-flex items-center gap-1"><Star className="h-3 w-3" /> Featured</span>}
          </div>
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">{labelOf(CATEGORIES, c.category)}</div>
      {c.bio && <p className="text-sm text-white/75 leading-snug">{c.bio}</p>}
      <div className="flex flex-wrap gap-1.5 mt-auto">
        {(c.social_links || []).map((s) => (
          <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer nofollow" data-testid={`creator-link-${s.platform}`}
            className="text-[11px] px-2.5 py-1 rounded-full border border-white/15 text-white/80 hover:border-lifted-green hover:text-lifted-green transition-colors inline-flex items-center gap-1">
            {labelOf(PLATFORMS, s.platform)} <ExternalLink className="h-3 w-3" />
          </a>
        ))}
      </div>
      {c.self_reported_followers && Object.keys(c.self_reported_followers).length > 0 && (
        <div className="text-[11px] text-white/50" data-testid="creator-followers">
          <Users className="h-3 w-3 inline mr-1" />
          {Object.entries(c.self_reported_followers).map(([k, v]) => `${labelOf(PLATFORMS, k)} ${Number(v).toLocaleString()}`).join(" · ")}
          <span className="italic"> (self-reported)</span>
        </div>
      )}
      {c.featured_content_url && (
        <a href={c.featured_content_url} target="_blank" rel="noopener noreferrer nofollow" className="text-xs text-lifted-green hover:underline" data-testid="creator-featured-link">
          View featured content →
        </a>
      )}
    </article>
  );
}

export function CreatorDirectory({ onApply }) {
  const [platform, setPlatform] = useState("");
  const [category, setCategory] = useState("");
  const [rows, setRows] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams();
    if (platform) p.set("platform", platform);
    if (category) p.set("category", category);
    api.get(`/creators?${p}`).then(({ data }) => setRows(data.creators || [])).catch(() => setRows([]));
  }, [platform, category]);

  const chip = (active) => `text-[10px] font-bebas uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border transition-colors ${
    active ? "bg-lifted-green text-black border-lifted-green" : "border-white/15 text-white/70 hover:text-white"}`;

  return (
    <section id="creators" className="py-16 lg:py-20 border-t border-white/5" data-testid="creator-directory">
      <div className="container mx-auto px-4">
        <div className="mb-8 max-w-2xl">
          <div className="eyebrow text-lifted-green">Creator Showcase</div>
          <h2 className="font-anton uppercase text-3xl lg:text-4xl text-white mt-2">Explore <span className="italic text-lifted-green">Creators</span></h2>
          <p className="text-white/60 text-sm mt-2">Opt-in profiles approved by the Lifted team. Links go to each creator's own public profiles.</p>
        </div>
        <div className="flex flex-wrap gap-2 mb-3" data-testid="directory-platform-filters">
          <button className={chip(!platform)} onClick={() => setPlatform("")} data-testid="dir-platform-all">All platforms</button>
          {PLATFORMS.map(([k, l]) => <button key={k} className={chip(platform === k)} onClick={() => setPlatform(k)} data-testid={`dir-platform-${k}`}>{l}</button>)}
        </div>
        <div className="flex flex-wrap gap-2 mb-8" data-testid="directory-category-filters">
          <button className={chip(!category)} onClick={() => setCategory("")} data-testid="dir-category-all">All categories</button>
          {CATEGORIES.map(([k, l]) => <button key={k} className={chip(category === k)} onClick={() => setCategory(k)} data-testid={`dir-category-${k}`}>{l}</button>)}
        </div>
        {rows === null ? (
          <div className="text-white/50 text-sm">Loading creators…</div>
        ) : rows.length === 0 ? (
          <div className="glass-panel p-8 text-center" data-testid="directory-empty">
            <p className="text-white/70">No approved creators match these filters yet.</p>
            <button onClick={onApply} className="btn-lifted mt-4" data-testid="directory-apply-cta">Apply as a Creator</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="directory-grid">
            {rows.map((c) => <CreatorCard key={c.id} c={c} />)}
          </div>
        )}
      </div>
    </section>
  );
}
